import { router } from "@inertiajs/react"
import { PlusCircle } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { categoriesPath } from "@/routes"
import type { Category } from "@/types"

const COLORS = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#f43f5e",
  "#0ea5e9",
  "#8b5cf6",
  "#f97316",
  "#64748b",
]

interface CategorySelectProps {
  value: string
  onValueChange: (value: string) => void
  categories: Category[]
  allowUncategorized?: boolean
  triggerClassName?: string
}

export function CategorySelect({
  value,
  onValueChange,
  categories,
  allowUncategorized = false,
  triggerClassName,
}: CategorySelectProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newName, setNewName] = useState("")
  const [newColor, setNewColor] = useState(COLORS[0])
  const [creating, setCreating] = useState(false)
  const pendingName = useRef<string | null>(null)

  useEffect(() => {
    if (pendingName.current) {
      const created = categories.find((c) => c.name === pendingName.current)
      if (created) {
        onValueChange(String(created.id))
        pendingName.current = null
      }
    }
  }, [categories, onValueChange])

  function handleSelectChange(val: string) {
    if (val === "__new__") {
      setNewName("")
      setNewColor(COLORS[0])
      setDialogOpen(true)
    } else {
      onValueChange(val)
    }
  }

  function handleCreate() {
    if (!newName.trim()) return
    setCreating(true)
    pendingName.current = newName.trim()

    router.post(
      categoriesPath(),
      { name: newName.trim(), category_type: "expense", color: newColor },
      {
        onSuccess: () => {
          setDialogOpen(false)
          setCreating(false)
        },
        onError: () => {
          setCreating(false)
          pendingName.current = null
        },
      },
    )
  }

  return (
    <>
      <Select value={value} onValueChange={handleSelectChange}>
        <SelectTrigger className={triggerClassName}>
          <SelectValue placeholder="Select a category" />
        </SelectTrigger>
        <SelectContent>
          {allowUncategorized && (
            <SelectItem value="uncategorized">
              <span className="text-muted-foreground">Uncategorized</span>
            </SelectItem>
          )}
          {categories.map((cat) => (
            <SelectItem key={cat.id} value={String(cat.id)}>
              <div className="flex items-center gap-2">
                <div
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: cat.color }}
                />
                {cat.name}
              </div>
            </SelectItem>
          ))}
          {categories.length > 0 && <SelectSeparator />}
          <SelectItem value="__new__">
            <div className="text-muted-foreground flex items-center gap-2">
              <PlusCircle className="h-3 w-3" />
              New Category
            </div>
          </SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Quick Create Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="new-cat-name">Name</Label>
              <Input
                id="new-cat-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Software"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleCreate()
                  }
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label>Color</Label>
              <div className="flex gap-2">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`h-7 w-7 rounded-full border-2 transition-transform ${
                      newColor === color
                        ? "border-foreground scale-110"
                        : "border-transparent"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
            >
              {creating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
